import { _stringMapEntries, localDate } from '@naturalcycles/js-lib';
import { dailyEntrySharedUtil } from './dailyEntry.shared.util';
class UserFertilitySharedUtil {
  parseUFRawString(ufString) {
    if (!ufString) return undefined;
    const ufRaw = JSON.parse(ufString);
    return this.parseUFRaw(ufRaw);
  }
  /**
   * Immutable.
   */
  parseUFRaw(ufRaw) {
    if (!ufRaw?.todayDate) return {};
    const uf = {
      // defaults
      ...ufRaw,
      // initialize empty maps to prevent mutation
      colorMap: {},
      predictionMap: {},
      entryMap: {}
    };
    _stringMapEntries(ufRaw.colorMap || {}).forEach(([date, cc]) => {
      uf.colorMap[date] = this.parseUFColorCode(cc, date);
    });
    _stringMapEntries(ufRaw.predictionMap || {}).forEach(([date, cc]) => {
      uf.predictionMap[date] = this.parseUFColorCode(cc, date);
    });
    Object.keys(ufRaw.entryMap || {}).forEach(i => {
      uf.entryMap[i] = dailyEntrySharedUtil.parseRawDailyEntry(ufRaw.entryMap[i]);
    });
    uf.lhdays || (uf.lhdays = []);
    uf.noOCycles = uf.noOCycles.map(Boolean); // disallow undefined/null
    uf.ovulationWindowMin || (uf.ovulationWindowMin = []);
    uf.ovulationWindowMax || (uf.ovulationWindowMax = []);
    uf.originalPredMensDays || (uf.originalPredMensDays = []);
    uf.mensLengths || (uf.mensLengths = []);
    uf.ovulationDays || (uf.ovulationDays = []);
    uf.cycleLengths || (uf.cycleLengths = []);
    uf.predOvulationDays || (uf.predOvulationDays = []);
    if (ufRaw.conceptionDate) {
      uf.dueDate || (uf.dueDate = localDate(ufRaw.conceptionDate).plus(267, 'day').toISODate());
    }
    return uf;
  }
  serializeToUFRawString(uf) {
    return JSON.stringify(this.serializeToUFRaw(uf));
  }
  serializeToUFRaw(uf) {
    if (!uf?.todayDate) return {};
    const r = {
      ...uf
    };
    r.colorMap = Object.fromEntries(_stringMapEntries(uf.colorMap || {}).map(([k, v]) => [k, this.serializeUFColorCode(v)]));
    r.predictionMap = Object.fromEntries(_stringMapEntries(uf.predictionMap || {}).map(([k, v]) => [k, this.serializeUFColorCode(v)]));
    r.entryMap = Object.fromEntries(_stringMapEntries(uf.entryMap || {}).map(([k, v]) => [k, dailyEntrySharedUtil.serializeToRawDailyEntry(v)]));
    // undefined => null (for Java compat)
    // UPD: not needed, because JSON.stringify([ undefined ]) === '[null]'
    // UPD2: actually needed to make back'n'forth conversion strictly identical (lossless)
    // UPD3: there's code (e.g pms reminder) that does `a === null` check, so, important to ensure it's null!
    r.ovulationDays = r.ovulationDays.map(undefinedToNull);
    r.cycleLengths = r.cycleLengths.map(undefinedToNull);
    r.ovulationWindowMin = r.ovulationWindowMin?.map(undefinedToNull);
    r.ovulationWindowMax = r.ovulationWindowMax?.map(undefinedToNull);
    r.originalPredMensDays = r.originalPredMensDays?.map(undefinedToNull);
    r.mensLengths = r.mensLengths?.map(undefinedToNull);
    r.predOvulationDays = r.predOvulationDays?.map(undefinedToNull);
    r.lhdays || (r.lhdays = []);
    return r;
  }
  parseUFColorCode(c, date) {
    const r = {
      date
    };
    const a = c.split(',');
    // For extra-safety and bw-compat, we check for existence even the properties that are not-null
    if (a[0]) r.color = Number(a[0]);
    if (a[1]) r.redscale = Number(a[1]);
    r.code = this.parseUFCode(a[2]);
    if (a[3]) r.cd = Number(a[3]);
    if (a[4] && a[4] !== 'null') r.cycleStartDate = a[4];
    if (a[5]) r.nCycle = Number(a[5]);
    if (a[6]) r.goal = Number(a[6]);
    if (a[7]) r.lhStatus = Number(a[7]);
    if (a[8]) r.ovulationStatus = Number(a[8]);
    if (a[9]) r.pregnancyTestStatus = Number(a[9]);
    if (a[10]) r.hwId = Number(a[10]);
    if (a[11]) r.dataFlags = a[11].split(';').map(Number);
    if (a[12]) r.mensQuantity = Number(a[12]);
    if (a[13]) r.predictedMensDate = a[13];
    return r;
  }
  serializeUFColorCode(cc) {
    const a = [
    // 0
    cc.color,
    // 1
    cc.redscale,
    // 2
    this.serializeUFCode(cc.code),
    // 3
    cc.cd,
    // 4, undefined in predictionMap
    cc.cycleStartDate,
    // 5
    cc.nCycle,
    // 6, undefined in predictionMap
    cc.goal,
    // 7
    cc.lhStatus,
    // 8
    cc.ovulationStatus,
    // 9
    cc.pregnancyTestStatus,
    // 10, undefined in predictionMap
    cc.hwId,
    // 11
    // dataFlags are serialized as e.g:
    // 4;5;6
    // `;` is the separator, cause we need a separator that's different from `,`
    // We cannot use JSON here (such as `[4, 5, 6]`), because of backwards compat (it's already NOT a Json string,
    // too late to change it)
    // Empty dataFlags would be serialized as empty string and empty-tail-optimized
    (cc.dataFlags || []).join(';'),
    // 12
    cc.mensQuantity,
    // 13
    cc.predictedMensDate];
    // We only scan down to index 6 (Goal), for b/w compatibility
    // Also because values up to 6 are always set (never empty)
    let lastNonEmpty = 6;
    for (let i = a.length - 1; i >= 7; i--) {
      if (a[i]) {
        lastNonEmpty = i;
        break;
      }
    }
    return a.slice(0, lastNonEmpty + 1).map(v => {
      // map undefined and null to empty string
      if (v === undefined || v === null) return '';
      return v;
    }).join(',');
  }
  parseUFCode(c = '') {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-string-starts-ends-with
      ovulation: c[0] === '1' || undefined,
      possPreg: c[1] === '1' || undefined,
      possMisc: c[2] === '1' || undefined,
      defPreg: c[3] === '1' || undefined,
      defMisc: c[4] === '1' || undefined,
      mens: c[5] === '1' || undefined,
      checkLh: c[6] === '1' || undefined,
      checkPreg: c[7] === '1' || undefined
    };
  }
  serializeUFCode(c) {
    const a = [
    // 0
    !!c.ovulation,
    // 1,
    !!c.possPreg,
    // 2
    !!c.possMisc,
    // 3
    !!c.defPreg,
    // 4
    !!c.defMisc,
    // 5
    !!c.mens,
    // 6
    !!c.checkLh,
    // 7
    !!c.checkPreg];
    // emptyTailOptimization
    // Special condition! Due to b/w compatibility with App<=3.2.13 Code string cannot be empty,
    // so, should contain at least one character, otherwise this condition breaks:
    // `if (a[2]) r.code = this.parseUFCode(a[2])`
    let lastNonEmpty = 0;
    for (let i = a.length - 1; i >= 1; i--) {
      if (a[i]) {
        lastNonEmpty = i;
        break;
      }
    }
    return a.slice(0, lastNonEmpty + 1).map(v => v ? '1' : '0').join('');
  }
}
export const userFertilitySharedUtil = new UserFertilitySharedUtil();
function undefinedToNull(v) {
  return v === undefined ? null : v;
}